import { ArrowLeft, ExternalLink, Plus, Trash2 } from "lucide-react";
import * as React from "react";
import { useForm } from "react-hook-form";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { z } from "zod";
import { Page } from "@/components/layout/page";
import { ConfirmDialog } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Callout, EmptyState, ErrorState, Spinner, TableSkeleton } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import { Input, Textarea } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/misc";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/toast";
import { FirmDetailsPanel } from "@/features/record-details";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { contactName, dialText, stageText } from "@/lib/contacts";
import {
  FIRM_STATUSES, PRIORITY_MAX, TAG_RE, firmChanges, firmStatusText, firmValues, firmsInState,
  normalizeTag, parseStates, priorityProblem, stateName, stateRank, statusWarning, type FirmValues,
} from "@/lib/firms";
import { useFormChanged, zodResolver } from "@/lib/form";
import { useAddFirmTag, useContacts, useFirm, useFirms, useRemoveFirmTag, useUpdateFirm } from "@/lib/queries";
import { atLeast, type Firm } from "@/lib/types";
import { formatDateTime, formatPhoneNumber, relativeTime } from "@/lib/utils";

/**
 * One firm (ADR-045): its book, its tags, its settings. The tab is in the URL. Supervisors read;
 * admins edit. A status change that stops the firm's dialing holds every list row of its leads in
 * the same commit as the save, and the Settings tab says so above Save.
 */

const TABS = ["contacts", "tags", "fields", "settings"] as const;
type Tab = (typeof TABS)[number];

export function FirmPage() {
  const { firmId } = useParams();
  const id = firmId && /^\d+$/.test(firmId) ? Number(firmId) : undefined;
  const [params, setParams] = useSearchParams();
  const requested = params.get("tab") ?? "";
  const tab: Tab = (TABS as readonly string[]).includes(requested) ? (requested as Tab) : "contacts";
  const { user } = useAuth();
  const canManage = atLeast(user?.role, "admin");
  const { data: firm, isPending, error, refetch } = useFirm(id);

  if (id === undefined || (error instanceof ApiError && error.status === 404)) {
    return (
      <Page>
        <EmptyState
          className="min-h-[60vh]"
          title="Firm not found"
          description="It may belong to another organisation."
          action={
            <Button variant="outline" asChild>
              <Link to="/firms">Back to firms</Link>
            </Button>
          }
        />
      </Page>
    );
  }
  if (isPending) {
    return (
      <Page>
        <div className="flex min-h-[40vh] items-center justify-center">
          <Spinner className="size-5" />
        </div>
      </Page>
    );
  }
  if (error || !firm) {
    return (
      <Page>
        <ErrorState
          className="min-h-[60vh]"
          error={error}
          title="Could not load the firm"
          action={
            <Button variant="outline" onClick={() => void refetch()}>
              Try again
            </Button>
          }
        />
      </Page>
    );
  }

  const status = firmStatusText(firm.status);
  return (
    <Page>
      <Link to="/firms" className="mb-3 inline-flex items-center gap-1 text-xs text-text-muted hover:text-text">
        <ArrowLeft className="size-3.5" />
        Firms
      </Link>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 space-y-1.5">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <h1 className="text-xl font-semibold tracking-tight text-text">{firm.label}</h1>
            <span className="font-mono text-sm text-text-muted">{firm.code}</span>
            <Badge tone={status.tone} title={status.description}>{status.label}</Badge>
          </div>
          <p className="text-sm text-text-muted">
            {[firm.states.join(", ") || null, firm.languages.map((l) => (l === "en" ? "English" : l === "es" ? "Spanish" : l)).join(", ")].filter(Boolean).join(" · ")}
            {/* The reason belongs to the pause: a firm resumed keeps the words on its row, and
                printing them beside Active would read as a contradiction. */}
            {firm.status === "paused" && firm.pause_reason && <> · {firm.pause_reason}</>}
          </p>
          <p className="text-xs text-text-muted">
            {firm.counts.contacts.toLocaleString()} contacts · {firm.counts.working.toLocaleString()} working ·{" "}
            {firm.counts.scheduled.toLocaleString()} booked · {firm.counts.exit.toLocaleString()} exits
            {firm.counts.held > 0 && <> · {firm.counts.held.toLocaleString()} held</>}
            {firm.dedicated_campaign_ids.length === 0
              ? " · worked on the shared calling campaign"
              : ` · ${firm.dedicated_campaign_ids.length} dedicated campaign`}
          </p>
        </div>
        <Button variant="outline" asChild>
          <Link to={`/contacts?firm=${encodeURIComponent(firm.code)}`}>
            <ExternalLink />
            Open contacts
          </Link>
        </Button>
      </div>
      <Card className="mt-5 overflow-hidden">
        <Tabs
          value={tab}
          onValueChange={(next) => {
            const p = new URLSearchParams(params);
            if (next === "contacts") p.delete("tab");
            else p.set("tab", next);
            setParams(p, { replace: true });
          }}
        >
          <TabsList className="px-4 pt-2">
            <TabsTrigger value="contacts">Contacts <span className="ml-1 text-text-subtle">{firm.counts.contacts.toLocaleString()}</span></TabsTrigger>
            <TabsTrigger value="tags">Tags <span className="ml-1 text-text-subtle">{firm.tags.length}</span></TabsTrigger>
            <TabsTrigger value="fields">Fields</TabsTrigger>
            <TabsTrigger value="settings">Settings</TabsTrigger>
          </TabsList>
          <TabsContent value="contacts">
            <FirmContacts firm={firm} />
          </TabsContent>
          <TabsContent value="tags" className="p-4">
            <FirmTags firm={firm} canManage={canManage} />
          </TabsContent>
          {/* The firm's custom fields (ACL-256): one panel of folders, each answer changed where it is read. */}
          <TabsContent value="fields" className="space-y-4 bg-surface-sunken p-4">
            <FirmDetailsPanel firmId={firm.id} />
          </TabsContent>
          <TabsContent value="settings" className="p-4">
            <FirmSettings firm={firm} canManage={canManage} />
          </TabsContent>
        </Tabs>
      </Card>
    </Page>
  );
}

/** The firm's newest contacts: the contacts query with the firm fixed, one page; the full list is a link. */
function FirmContacts({ firm }: { firm: Firm }) {
  const list = useContacts({ firm: firm.code }, 0);
  const rows = list.data?.items ?? [];
  return (
    <>
      {list.isPending ? (
        <TableSkeleton cols={5} rows={5} />
      ) : list.error ? (
        <ErrorState error={list.error} title="Could not load the contacts" />
      ) : rows.length === 0 ? (
        <EmptyState title="No contacts resolve to this firm" description="A lead names its firm by a tag; add the spellings the imports carry on the Tags tab." />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Stage</TableHead>
              <TableHead>Dial</TableHead>
              <TableHead>Created</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => {
              const stage = stageText(row.stage);
              const dial = dialText(row.dial, row.membership?.outcome_rep);
              return (
                <TableRow key={row.id}>
                  <TableCell>
                    <Link to={`/contacts/${row.id}`} className="font-medium text-text hover:underline">
                      {contactName(row) ?? <span className="text-text-muted">No name</span>}
                    </Link>
                  </TableCell>
                  <TableCell className="whitespace-nowrap font-mono text-xs">{formatPhoneNumber(row.phone)}</TableCell>
                  <TableCell><Badge tone={stage.tone} size="sm">{stage.label}</Badge></TableCell>
                  <TableCell><Badge tone={dial.tone} size="sm">{dial.label}</Badge></TableCell>
                  <TableCell className="whitespace-nowrap text-xs text-text-muted">{formatDateTime(row.created_at)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
      {(list.data?.total ?? 0) > rows.length && (
        <div className="border-t border-border px-4 py-2 text-xs text-text-muted">
          {rows.length} of {list.data?.total?.toLocaleString()} ·{" "}
          <Link to={`/contacts?firm=${encodeURIComponent(firm.code)}`} className="underline">
            open the full list
          </Link>
        </div>
      )}
    </>
  );
}

function FirmTags({ firm, canManage }: { firm: Firm; canManage: boolean }) {
  const toast = useToast();
  const add = useAddFirmTag();
  const remove = useRemoveFirmTag();
  const [tag, setTag] = React.useState("");
  const [removing, setRemoving] = React.useState<{ id: number; tag: string } | null>(null);
  const normalized = normalizeTag(tag);
  const problem = !tag.trim() ? null : !TAG_RE.test(normalized) ? "Letters, digits and underscores" : firm.tags.some((t) => t.tag === normalized) ? "Already one of this firm's tags" : null;

  return (
    <div className="space-y-4">
      <p className="text-sm text-text-muted">
        Every spelling a lead may carry to name this firm — intake's, the importer's, the hub's. The code is one of them and stays.
      </p>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Tag</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {firm.tags.map((t) => (
            <TableRow key={t.id}>
              <TableCell className="font-mono text-xs">
                {t.tag}
                {t.tag === firm.code && <Badge tone="outline" size="sm" className="ml-2">code</Badge>}
              </TableCell>
              <TableCell className="text-right">
                {canManage && t.tag !== firm.code && (
                  <Button variant="ghost" size="icon-sm" aria-label={`Remove ${t.tag}`} onClick={() => setRemoving(t)}>
                    <Trash2 />
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {canManage && (
        <form
          className="flex items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (problem || !normalized) return;
            add.mutate(
              { firmId: firm.id, tag: normalized },
              {
                onSuccess: () => {
                  setTag("");
                  toast.success("Alias added", `${normalized} now resolves to ${firm.label}`);
                },
                onError: (err) => toast.error("Could not add the alias", err instanceof ApiError ? err.message : "Please try again."),
              },
            );
          }}
        >
          <Field label="Add an alias" htmlFor="firm-tag" error={problem ?? undefined} hint={tag && !problem ? `Saved as ${normalized}` : "The next import resolves it to this firm"} className="w-72">
            <Input value={tag} onChange={(e) => setTag(e.target.value)} className="font-mono" placeholder="bernd_wa" autoComplete="off" />
          </Field>
          <Button type="submit" variant="outline" loading={add.isPending} disabled={!normalized || problem !== null} className="mb-6">
            <Plus />
            Add alias
          </Button>
        </form>
      )}
      <ConfirmDialog
        open={removing !== null}
        onOpenChange={(open) => !open && setRemoving(null)}
        title={`Remove ${removing?.tag}?`}
        description="Leads carrying only this tag stop resolving to the firm and show as Needs firm."
        confirmLabel="Remove"
        destructive
        loading={remove.isPending}
        onConfirm={() =>
          removing &&
          remove.mutate(
            { firmId: firm.id, tagId: removing.id },
            {
              onSuccess: () => setRemoving(null),
              onError: (err) => toast.error("Could not remove the alias", err instanceof ApiError ? err.message : "Please try again."),
            },
          )
        }
      />
    </div>
  );
}

const settingsSchema = z.object({
  code: z.string(),
  name: z.string().trim().min(1, "Required").max(200),
  label: z.string().trim().min(1, "Required").max(120),
  states: z.string().refine((v) => parseStates(v).problem === null, { message: "Two-letter state codes, comma-separated" }),
  state_priorities: z.record(
    z.string(),
    z.string().refine((v) => priorityProblem(v) === null, { message: `A whole number from 1 to ${PRIORITY_MAX}, or empty` }),
  ),
  languages: z.array(z.string()).min(1, "At least one language"),
  status: z.string(),
  pause_reason: z.string().max(500),
  contract_tag: z.string(),
});

/** The Contract tag picker's "none": a Select item cannot carry an empty value. */
const OWN_TAG = "__own__";

function FirmSettings({ firm, canManage }: { firm: Firm; canManage: boolean }) {
  const toast = useToast();
  const update = useUpdateFirm();
  const [formError, setFormError] = React.useState<string | null>(null);
  const form = useForm<FirmValues>({ resolver: zodResolver(settingsSchema), defaultValues: firmValues(firm) });
  const { register, handleSubmit, watch, setValue, reset, formState: { errors } } = form;
  const changed = useFormChanged(form);

  React.useEffect(() => {
    reset(firmValues(firm));
  }, [firm, reset]);

  const values = watch();
  const warning = statusWarning(firm.status, values.status, firm.counts.contacts);
  // The states the firm is ranked in are the ones typed above, once they read as states (ADR-170).
  const typedStates = parseStates(values.states);
  const rankStates = typedStates.problem === null ? typedStates.states : [];
  const others = (useFirms().data ?? []).filter((f) => f.id !== firm.id);

  const submit = handleSubmit((submitted) => {
    setFormError(null);
    const changes = firmChanges(submitted, firm);
    if (Object.keys(changes).length === 0) return;
    update.mutate(
      { id: firm.id, ...changes },
      {
        onSuccess: (saved) => toast.success("Firm saved", saved.label),
        onError: (e) => setFormError(e instanceof ApiError ? e.message : "Could not save the firm."),
      },
    );
  });

  const toggleLanguage = (code: string, on: boolean) => {
    const next = new Set(values.languages);
    if (on) next.add(code);
    else next.delete(code);
    setValue("languages", [...next], { shouldDirty: true, shouldValidate: true });
  };

  return (
    <form onSubmit={submit} noValidate className="max-w-2xl space-y-4">
      <fieldset disabled={!canManage} className="space-y-4">
        <Field label="Name" htmlFor="settings-name" required error={errors.name?.message}>
          <Input {...register("name")} autoComplete="off" />
        </Field>
        <Field label="Label" htmlFor="settings-label" required error={errors.label?.message} hint="What badges and chips print">
          <Input {...register("label")} autoComplete="off" />
        </Field>
        <Field label="Code" htmlFor="settings-code" hint="The canonical tag, immutable">
          <Input value={firm.code} readOnly className="bg-surface-sunken font-mono text-text-muted" />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="States" htmlFor="settings-states" error={errors.states?.message} hint="Two-letter codes, comma-separated">
            <Input {...register("states")} autoComplete="off" className="uppercase" />
          </Field>
          <fieldset>
            <legend className="mb-1.5 text-xs font-medium text-text">Languages</legend>
            <div className="flex gap-4 pt-2 text-sm">
              {(["en", "es"] as const).map((code) => (
                <label key={code} className="inline-flex items-center gap-1.5">
                  <input type="checkbox" className="accent-accent-9" checked={values.languages.includes(code)} onChange={(e) => toggleLanguage(code, e.target.checked)} />
                  {code === "en" ? "English" : "Spanish"}
                </label>
              ))}
            </div>
            {errors.languages?.message && <p className="mt-1 text-xs text-danger-11">{errors.languages.message}</p>}
          </fieldset>
        </div>
        <fieldset>
          <legend className="mb-1 text-xs font-medium text-text">Priority by state</legend>
          <p className="mb-2 text-xs text-text-muted">
            1 is the firm shown first for a contact in that state, on the Firms page and in the firm pickers. Empty puts it after the ranked firms.
          </p>
          {rankStates.length === 0 ? (
            <p className="text-xs text-text-subtle">Add a state above to rank the firm in it.</p>
          ) : (
            <div className="space-y-2">
              {rankStates.map((state) => {
                // The resolver keys a nested field's error by its dotted path, which `getFieldState` reads.
                const error = form.getFieldState(`state_priorities.${state}`, form.formState).error?.message;
                const rivals = firmsInState(others, state);
                return (
                  <div key={state} className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <label htmlFor={`settings-priority-${state}`} className="w-44 text-sm text-text">
                      {stateName(state)}
                    </label>
                    <Input
                      id={`settings-priority-${state}`}
                      {...register(`state_priorities.${state}`)}
                      type="number"
                      inputMode="numeric"
                      min={1}
                      max={PRIORITY_MAX}
                      step={1}
                      placeholder="—"
                      className="w-20"
                      aria-invalid={Boolean(error)}
                    />
                    <span className="text-xs text-text-muted">
                      {rivals.length === 0
                        ? "The only firm in this state"
                        : `Others: ${rivals.map((f) => `${f.label} ${stateRank(f, state) ?? "—"}`).join(" · ")}`}
                    </span>
                    {error && <p className="w-full text-xs text-danger-11">{error}</p>}
                  </div>
                );
              })}
            </div>
          )}
        </fieldset>
        <Field label="Status" htmlFor="settings-status" hint={firmStatusText(values.status).description}>
          <Select value={values.status} onValueChange={(v) => setValue("status", v, { shouldDirty: true })} disabled={!canManage}>
            <SelectTrigger id="settings-status" className="sm:w-60">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FIRM_STATUSES.map((s) => (
                <SelectItem key={s} value={s}>
                  {firmStatusText(s).label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        {values.status === "paused" && (
          <Field label="Pause reason" htmlFor="settings-reason" hint="Shown on every held lead" error={errors.pause_reason?.message}>
            <Textarea {...register("pause_reason")} rows={2} className="font-sans text-sm" />
          </Field>
        )}
        <Field
          label="Contract tag"
          htmlFor="settings-contract-tag"
          hint="The tag this firm's agreements are filed under in the contract service. Unset, the contact's own tag goes."
        >
          <Select
            value={values.contract_tag || OWN_TAG}
            onValueChange={(v) => setValue("contract_tag", v === OWN_TAG ? "" : v, { shouldDirty: true })}
            disabled={!canManage}
          >
            <SelectTrigger id="settings-contract-tag" className="sm:w-72">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={OWN_TAG}>The contact's own tag</SelectItem>
              {firm.tags.map((t) => (
                <SelectItem key={t.id} value={t.tag}>
                  <span className="font-mono">{t.tag}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      </fieldset>
      {warning && <Callout tone="warning">{warning}</Callout>}
      {formError && <Callout tone="danger">{formError}</Callout>}
      {canManage ? (
        <div className="flex items-center justify-end gap-2">
          <Button type="button" variant="ghost" disabled={!changed} onClick={() => reset(firmValues(firm))}>
            Discard
          </Button>
          <Button type="submit" loading={update.isPending} disabled={!changed}>
            Save
          </Button>
        </div>
      ) : (
        <p className="text-xs text-text-muted">An admin changes these. Last saved {relativeTime(firm.updated_at)}.</p>
      )}
    </form>
  );
}
